import type {AgentBuilderActions} from 'src/action-creators/agent-builder';
import type {
  AgentConfig,
  ChannelSettings,
  AgentStreamData,
  AgentReminderRules,
  JobMatchAttribute,
} from 'src/types/agent-builder';

import {
  RECEIVE_CHANNEL_SETTINGS,
  RECEIVE_AGENT_SUGGESTION,
  RECEIVE_AGENT_STREAM_DATA,
  RECEIVE_AGENT_CONFIGURATION,
  RECEIVE_AGENT_BUILDING,
  RECEIVE_AGENT_REMINDER,
  REQUEST_AGENT_ADDITIONAL_MODULES,
  RECEIVE_AGENT_ADDITIONAL_MODULES,
  REQUEST_AGENT_JOB_MATCH_ATTRIBUTES,
  RECEIVE_AGENT_JOB_MATCH_ATTRIBUTES,
} from 'src/action-creators/agent-builder';


interface AgentBuilderState {
  suggestion: string | null;
  streamData: Partial<AgentStreamData> | null;
  selectedAgent: AgentConfig | null;
  channelSettings: ChannelSettings | null;
  isAgentBuilding: boolean;
  agentReminder: AgentReminderRules | null;
  additionalModulesByAgentId: Record<string, string[]>;
  additionalModulesLoadingByAgentId: Record<string, boolean>;
  jobMatchAttributesByAgentId: Record<string, JobMatchAttribute[]>;
  jobMatchAttributesLoadingByAgentId: Record<string, boolean>;
}

const initialState: AgentBuilderState = {
  suggestion: null,
  streamData: null,
  selectedAgent: null,
  channelSettings: null,
  isAgentBuilding: false,
  agentReminder: null,
  additionalModulesByAgentId: {},
  additionalModulesLoadingByAgentId: {},
  jobMatchAttributesByAgentId: {},
  jobMatchAttributesLoadingByAgentId: {},
};

export default (
  state: AgentBuilderState = initialState,
  action: AgentBuilderActions,
): AgentBuilderState => {
  switch (action.type) {
    case RECEIVE_CHANNEL_SETTINGS:
      return {
        ...state,
        channelSettings: action.payload,
      };
    case RECEIVE_AGENT_SUGGESTION:
      return {
        ...state,
        suggestion: action.payload,
      };
    case RECEIVE_AGENT_CONFIGURATION:
      return {
        ...state,
        selectedAgent: action.payload
          ? {
              ...(state.selectedAgent || {}),
              ...action.payload,
            }
          : action.payload,
      };
    case RECEIVE_AGENT_STREAM_DATA:
      return {
        ...state,
        streamData: {
          ...(state.streamData || {}),
          ...action.payload,
        },
      };
    case RECEIVE_AGENT_BUILDING:
      return {
        ...state,
        isAgentBuilding: action.payload,
      };
    case RECEIVE_AGENT_REMINDER:
      return {
        ...state,
        agentReminder: action.payload,
      };
    case REQUEST_AGENT_ADDITIONAL_MODULES:
      return {
        ...state,
        additionalModulesLoadingByAgentId: {
          ...state.additionalModulesLoadingByAgentId,
          [action.payload]: true,
        },
      };
    case RECEIVE_AGENT_ADDITIONAL_MODULES: {
      const {agentId, modules} = action.payload;
      return {
        ...state,
        additionalModulesByAgentId: {
          ...state.additionalModulesByAgentId,
          [agentId]: modules,
        },
        additionalModulesLoadingByAgentId: {
          ...state.additionalModulesLoadingByAgentId,
          [agentId]: false,
        },
      };
    }
    case REQUEST_AGENT_JOB_MATCH_ATTRIBUTES:
      return {
        ...state,
        jobMatchAttributesLoadingByAgentId: {
          ...state.jobMatchAttributesLoadingByAgentId,
          [action.payload]: true,
        },
      };
    case RECEIVE_AGENT_JOB_MATCH_ATTRIBUTES: {
      const {agentId, attributes} = action.payload;
      return {
        ...state,
        jobMatchAttributesByAgentId: {
          ...state.jobMatchAttributesByAgentId,
          [agentId]: attributes,
        },
        jobMatchAttributesLoadingByAgentId: {
          ...state.jobMatchAttributesLoadingByAgentId,
          [agentId]: false,
        },
      };
    }
    default:
      return state;
  }
};
